import React from 'react';

import { Link, Image, Icon, Button, ButtonVariant, ChatLineIcon, WalletIcon } from '@/components';
import { PagePath, ProductStatus } from '@/layouts/shared';
import { createHandle, getProductImage, toMoney } from '@/libs/app';
import { TFunction } from 'next-i18next';
import { Product } from '@/features/livestream/detail/const';
import ProductAuction from './ProductAuction';
import { getAuth } from '@/libs/cookies';
import { useProfile } from '@/contexts/profile';
import { productUtils } from '@/libs/product';
type PinnedProductProps = {
  product: Product;
  productId: number;
  t: TFunction;
  isDisabled: boolean;
  userId: number;
  onConsultClick: () => void;
  liveStreamId: number;
  orderProductId: number | undefined;
};

export const PinnedProduct = (props: PinnedProductProps) => {
  const { isAuth } = getAuth();
  const profile: any = useProfile();

  const {
    product,
    productId,
    t,
    isDisabled,
    userId,
    onConsultClick,
    liveStreamId,
    orderProductId,
  } = props;

  const { hasDiscount, hasUserLevelDiscount, matchedUserLevelDiscount } =
    productUtils.preprocessProductDiscount(product, profile);

  if (matchedUserLevelDiscount) {
    product.product_discount = matchedUserLevelDiscount;
  }

  if (product.is_auction)
    return (
      <ProductAuction
        t={t}
        userId={userId}
        product={product}
        productId={productId}
        isDiscount={hasDiscount}
      />
    );

  return (
    <div className="tw-bg-white tw-rounded-5 tw-flex tw-gap-x-2 tw-p-2">
      <Link
        url={{
          pathname: PagePath.ProductDetailPage,
          query: {
            id: `${createHandle(product.product_name)}-${productId}-${product.product_code}`,
          },
        }}
      >
        <div
          className="tw-relative !tw-w-20 !tw-h-20 tw-bg-center tw-bg-no-repeat tw-bg-cover tw-rounded-5"
          style={{
            backgroundImage: `url('${getProductImage(product.images)}')`,
          }}
          title={product.product_name}
        >
          {hasDiscount && (
            <>
              <div className="tw-w-6 tw-h-6 tw-absolute tw-top-2 tw-right-2">
                <Image src="/sale.png" className="tw-w-full tw-h-full" alt="sale"></Image>
              </div>
            </>
          )}
        </div>
      </Link>
      <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-y-1">
        <Link
          className="tw-w-full tw-h-full"
          url={{
            pathname: PagePath.ProductDetailPage,
            query: {
              id: `${createHandle(product.product_name)}-${productId}-${product.product_code}`,
            },
          }}
        >
          <h4
            className="tw-text-primary-main tw-text-sm tw-font-semibold tw-line-clamp-1"
            dangerouslySetInnerHTML={{ __html: product.product_name }}
          />
        </Link>
        {!product.quotation ? (
          <>
            <div className="tw-text-secondary-70 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <span className="tw-text-13">{product.product_code}</span>
              <div className="tw-w-[2px] tw-h-[2px] tw-bg-secondary-70 tw-rounded-full"></div>
              {product.product_discount && hasDiscount ? (
                <div className="tw-flex">
                  <span className="tw-text-warning-90 tw-font-medium tw-text-13">
                    {toMoney(
                      product.price - (product.price * product.product_discount?.discount) / 100,
                      t
                    )}
                  </span>
                </div>
              ) : (
                <div className="tw-flex tw-text-xs tw-items-center lg:tw-gap-x-1">
                  <span className="tw-text-warning-90 tw-font-medium tw-text-13">
                    {toMoney(product.price, t)}
                  </span>
                </div>
              )}
            </div>
            {hasUserLevelDiscount && (
              <div className="tw-text-xs tw-mt-1">
                {!isAuth ? (
                  <span className="tw-text-secondary-70">Đăng nhập để xem ưu đãi của bạn</span>
                ) : matchedUserLevelDiscount ? (
                  <></>
                ) : (
                  <span className="tw-text-secondary-70">Hạng thẻ của bạn chưa có ưu đãi</span>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="tw-text-secondary-70">
              <span className="tw-text-13">{product.product_code}</span>
              <div className="tw-text-warning-90">{t('layout:btn.contact')}</div>
            </div>
          </>
        )}

        {!product.quotation && (
          <div className="tw-flex tw-justify-end tw-gap-x-4 lg:tw-gap-x-2 xl:tw-gap-x-4 tw-text-xs tw-items-center">
            {isDisabled || product.quantity <= 0 || orderProductId === productId ? (
              <Button
                disabled={!userId}
                className="!tw-rounded-5 tw-py-1 tw-uppercase  tw-px-3 tw-opacity-70 tw-flex tw-items-center !tw-w-[120px] lg:!tw-w-[110px] xl:!tw-w-[120px]"
                variant={ButtonVariant.Outlined}
              >
                <Icon
                  icon={<ChatLineIcon />}
                  className="tw-w-4 tw-h-4 tw-mr-3 lg:tw-mr-2 xl:tw-mr-3"
                />
                {t('layout:btn.consult')}
              </Button>
            ) : (
              <Button
                disabled={!userId}
                onClick={onConsultClick}
                className="!tw-rounded-5 tw-py-1 tw-uppercase  tw-px-3 tw-opacity-70 tw-flex tw-items-center !tw-w-[120px] lg:!tw-w-[110px] xl:!tw-w-[120px] tw-text-base lg:tw-text-sm xl:tw-text-base"
                variant={ButtonVariant.Outlined}
              >
                <Icon
                  icon={<ChatLineIcon />}
                  className="tw-w-4 tw-h-4 tw-mr-3 lg:tw-mr-2 xl:tw-mr-3"
                />
                {t('layout:btn.consult')}
              </Button>
            )}
            {product.quantity <= 0 ||
            product.status != ProductStatus.Availability ||
            orderProductId === productId ? (
              <Button
                disabled
                className="!tw-rounded-5 tw-text-white tw-bg-primary-main  tw-py-1 tw-px-3 tw-flex tw-items-center tw-justify-center !tw-w-max lg:!tw-w-[125px] xl:!tw-w-max  tw-uppercase tw-text-base lg:tw-text-sm xl:tw-text-base"
              >
                <Icon
                  icon={<WalletIcon />}
                  className="tw-w-4 tw-h-4 tw-mr-3 lg:tw-mr-2 xl:tw-mr-3 tw-text-white"
                />
                {t('layout:btn.deposited')}
              </Button>
            ) : (
              <Link
                url={{
                  pathname: PagePath.ProductDepositPage,
                  query: { id: productId, stream_id: liveStreamId },
                }}
                target="_blank"
                className="tw-bg-primary-main !tw-text-white !tw-w-max lg:!tw-w-[125px] xl:!tw-w-max tw-gap-x-1 tw-border tw-px-3 tw-py-1 tw-rounded-5 tw-flex tw-items-center tw-justify-center  tw-uppercase tw-text-base lg:tw-text-sm xl:tw-text-base"
              >
                <Icon
                  icon={<WalletIcon />}
                  className="tw-w-4 tw-h-4 tw-mr-3 lg:tw-mr-2 xl:tw-mr-3 tw-text-white"
                />
                <p>{t('layout:btn.order')}</p>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
